import { forwardRef, useCallback, useState } from 'react';
import { useApp } from '@context/AppContext';
import { useGetAccount } from '@features/user/hooks/useGetAccount';
import { useUpdateAccount } from '@features/user/hooks/useUpdateAccount';
import { DrawerForm } from '@goodfynd/forms';
import type { DrawerRefMethods } from '@goodfynd/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { accountApiKeys } from '@hooks/api/account/useAccountApi';
import type { UserProfileRequest } from 'types/auth';

import type {
  UserProfileFormDrawerProps,
  UserProfileFormFields,
} from './types';
import { UserProfileForm } from './UserProfileForm';
import { userProfileValidation } from './validation';

export const UserProfileFormDrawer = forwardRef(function UserProfileFormDrawer(
  { onClose, onOpen, ...props }: UserProfileFormDrawerProps,
  ref: React.ForwardedRef<DrawerRefMethods | null>
) {
  const [isOpen, setIsOpen] = useState(props.defaultOpen);
  const { user } = useApp();

  const {
    data,
    isLoading,
    remove: removeAccountData,
  } = useGetAccount(
    {
      id: isOpen ? user?.id : '',
    },
    { queryKey: accountApiKeys.get(`${user?.id}-form`) }
  );

  const onSuccess = useCallback(() => {
    removeAccountData();
  }, [removeAccountData]);

  const { updateAccount } = useUpdateAccount({
    onSuccess,
  });

  return (
    <DrawerForm<UserProfileFormFields>
      {...props}
      busy={isLoading}
      defaultValues={{ ...data, file: data?.avi }}
      ref={ref}
      renderFields={(props) => <UserProfileForm {...props} />}
      resolver={zodResolver(userProfileValidation)}
      targetContent="Update profile"
      title="Update profile"
      onClose={() => {
        setIsOpen(false);
        onClose?.();
      }}
      onOpen={() => {
        setIsOpen(true);
        onOpen?.();
      }}
      onSubmit={({ file, ...data }) => {
        const params: UserProfileRequest = {
          ...data,
          file: file?.startsWith('http') ? undefined : file,
        };

        return updateAccount(params);
      }}
    />
  );
});
